/**
 * Text formatting utilities for displaying translations
 */

/**
 * Split text into sentences for better readability.
 * Handles multiple languages and edge cases like abbreviations, decimals, etc.
 * 
 * @param text - The text to split into sentences
 * @returns Array of sentence strings
 */
export function splitIntoSentences(text: string): string[] {
  if (!text || !text.trim()) {
    return [];
  }

  // Trim the input text
  const trimmedText = text.trim();

  // Split by sentence-ending punctuation followed by whitespace or end of string
  // This regex handles:
  // - Periods (.)
  // - Exclamation marks (!)
  // - Question marks (?)
  // - Chinese full stops (。)
  // - Chinese exclamation marks (！)
  // - Chinese question marks (？)
  // - Followed by whitespace or end of string
  const sentenceEndings = /([.!?。！？]+)(\s+|$)/g;
  
  const sentences: string[] = [];
  let lastIndex = 0;
  let match;

  // Find all sentence endings
  while ((match = sentenceEndings.exec(trimmedText)) !== null) {
    const sentenceEnd = match.index + match[1].length;
    const sentence = trimmedText.substring(lastIndex, sentenceEnd).trim();
    
    if (sentence) {
      sentences.push(sentence);
    }
    
    lastIndex = sentenceEnd + match[2].length;
  }

  // Add remaining text if any
  const remaining = trimmedText.substring(lastIndex).trim();
  if (remaining) {
    sentences.push(remaining);
  }

  // If no sentences were found (no sentence-ending punctuation), return the whole text
  if (sentences.length === 0) {
    return [trimmedText];
  }

  return sentences;
}

/**
 * Format text by splitting into sentences and cleaning up whitespace.
 * 
 * @param text - The text to format
 * @returns Array of formatted sentence strings
 */
export function formatTranslationText(text: string): string[] {
  const sentences = splitIntoSentences(text);
  
  // Clean up each sentence: remove extra whitespace, normalize spaces
  return sentences.map(sentence => 
    sentence.replace(/\s+/g, ' ').trim()
  ).filter(sentence => sentence.length > 0);
}


